import { useFetch } from "app/queryProvider";
import { useAuth } from "app/auth/hooks/useAuth";
import { useNavigation } from "@react-navigation/native";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { TNavigationStack } from "app/types";
import { IPathwayActivitiesResponse, IPathwayActivity, ActivityType } from "app/_modules/pathways/entities";
import { getCurrentTimeOfDay } from "@shared/utils/timeOfDay";
import { ACTIVITY_STATUS } from "app/_modules/core/constants";

export const useActivitySequence = (currentActivityId: string) => {
  const { meData } = useAuth();
  const navigation = useNavigation<NativeStackNavigationProp<TNavigationStack>>();

  const { data: pathwayActivities } = useFetch<IPathwayActivitiesResponse>({
    url: `/activities?date=${new Date().toISOString().split('T')[0]}`,
    queryKey: [["pathway", "activities"]],
    resource: { name: "pathways", id: meData?.current_pathway.id },
    options: {
      enabled: !!meData?.current_pathway.id
    }
  });

  const getPeriodActivities = (): IPathwayActivity[] => {
    if (!pathwayActivities) return [];
    const timeOfDayKey = getCurrentTimeOfDay();
    return pathwayActivities[timeOfDayKey] ?? [];
  };

  const getRemainingActivities = (): IPathwayActivity[] =>
    getPeriodActivities().filter(
      a => a.status !== ACTIVITY_STATUS.COMPLETED && a.activityId !== currentActivityId
    );

  const hasRemainingActivitiesInCurrentPeriod = () => getRemainingActivities().length > 0;

  const getNextActivity = (): IPathwayActivity | undefined => getRemainingActivities()[0];

  const navigateToNextOrHome = () => {
    const nextActivity = getNextActivity();

    if (nextActivity) {
      switch (nextActivity.activityType) {
        case ActivityType.PATHWAY_VITAL_PARAMETER:
          navigation.push('MeasurementFlow', {
            activityName: nextActivity.activityName,
            activityId: nextActivity.activityId,
            initialStep: 0, 
            forceReset: true 
          });
          break;
        case ActivityType.PATHWAY_DRUG:
          navigation.push('DrugIntakeFlow', {
            activityName: nextActivity.activityName,
            activityId: nextActivity.activityId,
            bucket: getCurrentTimeOfDay(),
            initialStep: 0,
            forceReset: true
          });
          break;
        case ActivityType.PATHWAY_QUESTIONNAIRE:
          navigation.push('QuestionnaireFlow', {
            activityName: nextActivity.activityName,
            activityId: nextActivity.activityId,
            initialStep: 0, 
            forceReset: true
          });
          break;
        default:
          navigation.goBack();
      }
    } else {
      navigation.goBack();
    }
  };

  return {
    hasRemainingActivitiesInCurrentPeriod,
    getNextActivity,
    navigateToNextOrHome,
    pathwayActivities
  };
};
